import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Search, ArrowRight, GraduationCap, Globe, BookOpen, Building2, ShieldCheck, Trophy } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { initials, resolveImg } from "@/lib/format";

export default function Home() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/public/homepage").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const submit = (e) => {
    e.preventDefault();
    navigate(`/courses?q=${encodeURIComponent(q)}`);
  };

  const stats = data?.stats || {};

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1513258496099-48168024aec0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="hero-overlay absolute inset-0" />
        <div className="relative mx-auto max-w-4xl px-4 py-20 text-center md:py-28">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-white backdrop-blur">
            <ShieldCheck className="h-3.5 w-3.5" /> Trusted online education directory
          </span>
          <h1 className="mt-5 font-heading text-4xl font-bold tracking-tight text-white sm:text-5xl lg:text-6xl">
            Find the Right Online Course
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-base text-slate-200 md:text-lg">
            Explore online degrees, diplomas and professional programmes from universities and colleges around the world.
          </p>
          <form onSubmit={submit} className="mx-auto mt-8 max-w-2xl">
            <div className="flex flex-col gap-2 rounded-xl bg-white p-2 shadow-2xl sm:flex-row">
              <div className="flex flex-1 items-center gap-2 px-3">
                <Search className="h-5 w-5 shrink-0 text-slate-400" />
                <Input
                  data-testid="hero-search-input"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search by course, university or specialisation..."
                  className="border-0 text-base shadow-none focus-visible:ring-0"
                />
              </div>
              <Button type="submit" size="lg" className="bg-blue-800 hover:bg-blue-900" data-testid="hero-search-btn">
                SEARCH
              </Button>
            </div>
          </form>
          <div className="mt-4 flex flex-wrap justify-center gap-2 text-sm text-slate-200">
            <span>Popular:</span>
            {["Online MBA", "Online MCA", "Data Science", "Online B.Com"].map((t) => (
              <button
                key={t}
                onClick={() => navigate(`/courses?q=${encodeURIComponent(t)}`)}
                className="rounded-full bg-white/10 px-3 py-0.5 backdrop-blur transition hover:bg-white/20"
              >
                {t}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto grid max-w-6xl grid-cols-2 gap-6 px-4 py-8 md:grid-cols-4 md:px-6">
          {[
            { icon: Building2, label: "Universities", value: stats.institutions },
            { icon: BookOpen, label: "Online Programmes", value: stats.courses },
            { icon: Globe, label: "Countries", value: stats.countries },
            { icon: GraduationCap, label: "Categories", value: stats.categories },
          ].map((s) => (
            <div key={s.label} className="text-center">
              <s.icon className="mx-auto h-6 w-6 text-blue-800" />
              <p className="mt-2 font-heading text-2xl font-bold text-slate-900">{s.value ?? "—"}+</p>
              <p className="text-sm text-slate-500">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* World's Top Universities */}
      {data?.top_universities?.length > 0 && (
        <section className="border-b border-slate-200 bg-white py-14">
          <div className="mx-auto max-w-7xl px-4 md:px-6">
            <div className="mb-8 text-center">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700">
                <Trophy className="h-3.5 w-3.5" /> Globally recognised
              </span>
              <h2 className="mt-3 font-heading text-2xl font-bold tracking-tight text-slate-900 md:text-3xl">
                World's Top Universities
              </h2>
              <p className="mx-auto mt-1 max-w-2xl text-slate-500">
                Study online with some of the most respected institutions in the world
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
              {data.top_universities.map((u) => (
                <Link
                  key={u.id}
                  to={`/universities/${u.slug}`}
                  data-testid={`top-uni-${u.slug}`}
                  className="group flex flex-col items-center rounded-xl border border-slate-200 bg-white p-5 text-center transition-all hover:border-amber-300 hover:shadow-md"
                >
                  {u.logo ? (
                    <img src={resolveImg(u.logo)} alt={u.name} className="h-16 w-16 rounded-full object-cover ring-2 ring-slate-100" />
                  ) : (
                    <span className="flex h-16 w-16 items-center justify-center rounded-full bg-blue-50 font-heading text-lg font-bold text-blue-800">{initials(u.name)}</span>
                  )}
                  <h3 className="mt-3 font-heading text-sm font-semibold leading-tight text-slate-900 group-hover:text-blue-800">{u.name}</h3>
                  <p className="mt-1 text-xs text-slate-500">{u.country}</p>
                  <p className="mt-2 text-xs font-medium text-amber-700">{u.course_count} programmes</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Popular categories */}
      <section className="mx-auto max-w-7xl px-4 py-16 md:px-6">
        <div className="mb-8 flex items-end justify-between">
          <div>
            <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900 md:text-3xl">Popular Online Courses</h2>
            <p className="mt-1 text-slate-500">Browse programmes by category</p>
          </div>
          <Link to="/categories" className="hidden text-sm font-medium text-blue-800 hover:underline sm:block">
            View all categories
          </Link>
        </div>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          {(data?.categories || []).map((c) => (
            <Link
              key={c.id}
              to={`/courses/${c.slug}`}
              data-testid={`category-${c.slug}`}
              className="group rounded-xl border border-slate-200 bg-white p-5 transition-all hover:border-blue-300 hover:shadow-md"
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 text-blue-800 transition group-hover:bg-blue-800 group-hover:text-white">
                <GraduationCap className="h-5 w-5" />
              </span>
              <h3 className="mt-3 font-heading font-semibold text-slate-900">{c.name}</h3>
              <p className="mt-1 text-xs text-slate-500">{c.course_count} programmes</p>
            </Link>
          ))}
        </div>
      </section>

      {/* Featured universities */}
      {data?.featured?.length > 0 && (
        <section className="bg-slate-50 py-16">
          <div className="mx-auto max-w-7xl px-4 md:px-6">
            <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900 md:text-3xl">Featured Universities</h2>
            <p className="mt-1 text-slate-500">Recognised institutions offering online programmes</p>
            <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {data.featured.map((u) => (
                <Link
                  key={u.id}
                  to={`/universities/${u.slug}`}
                  data-testid={`featured-uni-${u.id}`}
                  className="group flex flex-col rounded-xl border border-slate-200 bg-white p-6 transition-all hover:shadow-lg"
                >
                  <div className="flex items-center gap-3">
                    {u.logo ? (
                      <img src={resolveImg(u.logo)} alt={u.name} className="h-12 w-12 rounded-md object-cover" />
                    ) : (
                      <span className="flex h-12 w-12 items-center justify-center rounded-md bg-blue-50 font-heading font-bold text-blue-800">{initials(u.name)}</span>
                    )}
                    <div>
                      <h3 className="font-heading font-semibold text-slate-900 group-hover:text-blue-800">{u.name}</h3>
                      <p className="text-xs text-slate-500">{[u.city, u.country].filter(Boolean).join(", ")}</p>
                    </div>
                  </div>
                  <p className="mt-3 line-clamp-2 text-sm text-slate-500">{u.description}</p>
                  <div className="mt-4 flex items-center justify-between text-sm">
                    <span className="text-slate-500">{u.course_count} programmes</span>
                    <span className="font-medium text-blue-800">View <ArrowRight className="inline h-4 w-4" /></span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA for universities */}
      <section className="mx-auto max-w-7xl px-4 py-16 md:px-6">
        <div className="rounded-2xl bg-blue-800 px-6 py-12 text-center md:px-12">
          <h2 className="font-heading text-2xl font-bold text-white md:text-3xl">Are you a university or college?</h2>
          <p className="mx-auto mt-3 max-w-xl text-blue-100">
            List your online programmes and reach students discovering courses worldwide. Free and paid listings available.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Button asChild size="lg" className="bg-white text-blue-800 hover:bg-slate-100" data-testid="cta-register-btn">
              <Link to="/register">List Your Courses</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white bg-transparent text-white hover:bg-white/10">
              <Link to="/pricing">View Listing Plans</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
